<template>
  <div>
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="items"
      :server-items-length="meta.total"
      :footer-props="footerProps"
      :options="options"
      :page="page"
      :items-per-page="itemsPerPage"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :multi-sort="multiSort"
      v-on="listeners"
    >
      <template v-slot:item.building_complex="{ item }">
        <span class="mr-2">{{ item.building_complex }}</span>
        <router-link
          v-if="item.building_complex_id"
          class="text-decoration-none"
          target="_blank"
          :to="{
            name: 'building_complexes.show',
            params: { id: item.building_complex_id }
          }"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">
                mdi-eye-outline
              </v-icon>
            </template>
            <span v-if="item.building_complex">{{
              item.building_complex
            }}</span>
            <span v-else>{{ $t('ITEM.VIEW') }}</span>
          </v-tooltip>
        </router-link>
      </template>
      <template v-slot:item.order_number="{ item }">
        <span class="mr-2">{{ item.order_number }}</span>
        <router-link
          v-if="item.order_type_id && item.order_id"
          class="text-decoration-none"
          target="_blank"
          :to="getOrderRoute(item)"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">mdi-eye-outline</v-icon>
            </template>
            <span v-if="item.order_number">{{ item.order_number }}</span>
            <span v-else>{{ $t('ITEM.VIEW') }}</span>
          </v-tooltip>
        </router-link>
      </template>

      <template v-slot:item.service_title="{ item }">
        <span class="mr-2">{{ item.service_title }}</span>
        <router-link
          class="text-decoration-none"
          target="_blank"
          :to="
            item.service_type === 2
              ? {
                  name: 'customer-service.show',
                  params: { id: item.service_id }
                }
              : item.service_type === 3
              ? {
                  name: 'internal-service.show',
                  params: { id: item.service_id }
                }
              : ''
          "
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">mdi-eye-outline</v-icon>
            </template>
            <span v-if="item.service_title">{{ item.service_title }}</span>
            <span v-else>{{ $t('ITEM.VIEW') }}</span>
          </v-tooltip>
        </router-link>
      </template>

      <template v-slot:item.date_from="{ item }">
        <span class="mr-2">{{ item.date_from }}</span>

        <v-tooltip v-if="item.report_time" top>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-clock-time-four-outline</v-icon>
          </template>
          {{ $t('TASKS.REPORT_TIME') }}: {{ item.report_time }}
        </v-tooltip>
      </template>

      <template v-slot:item.employee="{ item }">
        <span class="mr-2">{{ item.employee }}</span>
        <router-link
          class="text-decoration-none"
          target="_blank"
          :to="{
            name: 'employees.show',
            params: { id: item.employee_id }
          }"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">mdi-eye-outline</v-icon>
            </template>
            <span v-if="item.employee">{{ item.employee }}</span>
            <span v-else>{{ $t('ITEM.VIEW') }}</span>
          </v-tooltip>
        </router-link>
      </template>

      <template v-slot:item.customer="{ item }">
        <span class="mr-2">{{ item.customer }}</span>
        <router-link
          class="text-decoration-none"
          target="_blank"
          :to="
            item.person_id
              ? {
                  name: 'persons.show',
                  params: { id: item.person_id }
                }
              : item.company_id
              ? {
                  name: 'companies.show',
                  params: { id: item.company_id }
                }
              : ''
          "
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">mdi-eye-outline</v-icon>
            </template>
            <span v-if="item.customer">{{ item.customer }}</span>
            <span v-else>{{ $t('ITEM.VIEW') }}</span>
          </v-tooltip>
        </router-link>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip :color="item.status_color" :text-color="item.status_font_color">
          {{ item.status }}
        </v-chip>
      </template>

      <template v-slot:item.action="{ item }">
        <div class="text-center">
          <v-list-item @click="$emit('show', item)">
            <v-list-item-title>
              <v-icon class="mr-1" small>mdi-eye</v-icon>
              {{ $t('ITEM.VIEW') }}
            </v-list-item-title>
          </v-list-item>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { dateFormat } from '@/modules/_global/filters';
import { getViewRouteNameByOrderType } from '@/modules/orders/common/utils';
import listItems from '@/modules/_global/mixins/list-items';

export default {
  name: 'TasksList',

  filters: {
    dateFormat
  },

  mixins: [listItems],

  data: () => ({
    unwatch: null
  }),

  computed: {
    headers() {
      return [
        {
          text: this.$t('ITEM.ID'),
          sortable: true,
          value: 'id'
        },
        {
          text: this.$t('TASKS.ORDER_ID'),
          sortable: true,
          value: 'order_number'
        },
        {
          text: this.$t('EMPLOYEES.ITEM'),
          sortable: true,
          value: 'employee'
        },
        {
          text: this.$t('TASKS.SERVICE'),
          sortable: true,
          value: 'service_title'
        },
        {
          text: this.$t('BUILDING_COMPLEXES.ITEM'),
          sortable: true,
          value: 'building_complex'
        },
        {
          text: this.$t('PROFILE.CITY'),
          sortable: true,
          value: 'city'
        },
        {
          text: this.$t('ORDERS.CUSTOMER'),
          sortable: true,
          value: 'customer'
        },
        {
          text: this.$t('ITEM.DATE'),
          sortable: true,
          value: 'date_from'
        },
        {
          text: this.$t('ITEM.TIME'),
          sortable: false,
          value: 'time'
        },
        {
          text: this.$t('EMPLOYEES.STATUS'),
          sortable: true,
          value: 'status'
        },
        {
          text: this.$t('DATA_TABLE.ACTIONS'),
          value: 'action',
          sortable: false,
          align: 'center'
        }
      ];
    }
  },

  created() {
    this.unwatch = this.$store.watch(
      state => state.localization.lang,
      () => {
        const lang = this.$store.state.localization.lang;

        if (lang) {
          this.refreshData();
        }
      }
    );
  },

  destroyed() {
    this.unwatch();
  },

  methods: {
    getOrderRoute(item) {
      const routeName = getViewRouteNameByOrderType(item.order_type_id);

      if (routeName) {
        return {
          name: routeName,
          params: { id: item.order_id }
        };
      }
    }
  }
};
</script>
